import { useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import {
  NotificationApi,
  notificationApiRef,
} from '../../../../../apis/notificationApi';
import { toolsProvisioningApiRef } from '../../../../../apis/toolsProvisioningApi';
import {
  ProjectMappingType,
  MappedProject,
} from '../interfaces/ui/addProjectModal';
import { TestPlan } from '../interfaces/ui/testPlan';
import { mapTestPlanResponse } from '../dto/responseDto/testPlan.response.dto';

export const useProjectTestPlans = () => {
  const notificationApi: NotificationApi = useApi(notificationApiRef);
  const toolsProvisioningApi = useApi(toolsProvisioningApiRef);

  const [testPlans, setTestPlans] = useState<TestPlan[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProjectTestPlans = async (
    apmNumber: string,
    project: MappedProject,
    projectMappingType: ProjectMappingType,
  ) => {
    if (
      !project.projectId ||
      (projectMappingType === 'ADO' && !project.projectOrg)
    ) {
      setTestPlans([]);
      return;
    }

    setLoading(true);

    try {
      let response;
      if (projectMappingType === 'ADO') {
        response = await toolsProvisioningApi.getAdoProjectTestPlans(
          apmNumber,
          project.projectId,
          project.projectName,
          project.projectOrg!,
        );
      }

      if (projectMappingType === 'JIRA') {
        response = await toolsProvisioningApi.getJiraProjectTestPlans(
          apmNumber,
          project.projectId,
          project.projectName,
          project.projectKey!,
        );
      }

      if (!response || response?.status?.toLowerCase() !== 'success') {
        throw new Error(
          `Failed to fetch test plans for selected project. Please try again.`,
        );
      }

      const testPlans: TestPlan[] = mapTestPlanResponse(
        response,
        projectMappingType,
      );

      if (testPlans.length === 0) {
        setError(
          `No test plans found for the selected project in ${projectMappingType}. Please ensure that test plans exist and try again.`,
        );
        setTestPlans([]);
        return;
      }

      setTestPlans(testPlans);
    } catch (error) {
      setTestPlans([]);
      setError(`Error occurred - ${error}`);
      notificationApi.sendNotification({
        message: `Error occurred - ${error}`,
        disapperAfterMs: 4000,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const clearTestPlansFetchError = () => setError(null);

  return {
    fetchProjectTestPlans,
    loadingTestPlans: loading,
    testPlans,
    testPlansError: error,
    clearTestPlansFetchError,
  };
};
